import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Bell, Music, Heart, Check, Loader2, Users2, Building2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";

const TYPE_META = {
  new_release:   { icon: Music,     color: "#ec4899" },
  new_follower:  { icon: Heart,     color: "#3b82f6" },
  collab:        { icon: Users2,    color: "#60a5fa" },
  label_request: { icon: Building2, color: "#34d399" },
};

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)    return "Gerade eben";
  if (diff < 3600)  return `Vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `Vor ${Math.floor(diff / 3600)} Std.`;
  return `Vor ${Math.floor(diff / 86400)} Tagen`;
}

function NotifItem({ n, onRead }) {
  const Meta = TYPE_META[n.type] || { icon: Bell, color: "#52525b" };
  const Icon = Meta.icon;

  return (
    <div
      data-testid={`notif-item-${n.id}`}
      className="px-5 py-4 transition-all duration-150 cursor-pointer"
      style={{
        background: n.read ? "transparent" : "rgba(236,72,153,0.03)",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
      }}
      onClick={() => !n.read && onRead(n.id)}
    >
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: `${Meta.color}18`, border: `1px solid ${Meta.color}25` }}>
          <Icon size={15} style={{ color: Meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <p className={`text-sm font-semibold leading-snug ${n.read ? "text-zinc-400" : "text-white"}`}>
              {n.title}
            </p>
            {!n.read && (
              <span className="w-2 h-2 rounded-full bg-rose-400 flex-shrink-0 mt-1.5" />
            )}
          </div>
          {/* Vollständige Nachricht – kein Abschneiden */}
          <p className="text-zinc-400 text-sm mt-1 leading-relaxed whitespace-pre-wrap break-words">
            {n.message}
          </p>
          {n.link && (
            <Link
              to={n.link}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-rose-400 hover:text-rose-300 transition-colors"
            >
              Ansehen →
            </Link>
          )}
          <p className="text-zinc-600 text-xs mt-2">{timeAgo(n.created_at)}</p>
        </div>
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  const { t } = useTranslation();
  const { isPublicAuth, token } = usePublicAuth();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    if (!isPublicAuth) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get("/notifications/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data || []);
    } catch {}
    setLoading(false);
  }, [isPublicAuth, token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const markRead = async (id) => {
    await api.post("/notifications/my/mark-read", { id }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    await api.post("/notifications/my/mark-read", {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setItems(prev => prev.map(n => ({ ...n, read: true })));
  };

  const unreadCount = items.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-[#060606] text-white">
      <Helmet>
        <title>{`${t("collab.notif_page_title")} – HUWND`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(236,72,153,0.1)", border: "1px solid rgba(236,72,153,0.2)" }}>
              <Bell size={18} className="text-rose-400" />
            </div>
            <div>
              <h1 className="text-xl font-black text-white">{t("collab.notif_page_title")}</h1>
              {unreadCount > 0 && (
                <p className="text-xs text-rose-400 mt-0.5">
                  {unreadCount} {t("collab.notif_new")}
                </p>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              data-testid="notif-mark-all-read"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-zinc-400 hover:text-zinc-200 transition-all border border-white/[0.06] hover:bg-white/[0.04]"
            >
              <Check size={12} /> {t("collab.notif_mark_all")}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="rounded-2xl overflow-hidden" style={{
          background: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}>
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 size={20} className="animate-spin text-zinc-600" />
            </div>
          ) : !isPublicAuth ? (
            <div className="py-16 text-center">
              <Bell size={28} className="mx-auto mb-3 text-zinc-700" />
              <p className="text-zinc-500 text-sm">Bitte einloggen</p>
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <Bell size={28} className="mx-auto mb-3 text-zinc-700" />
              <p className="text-zinc-500 text-sm">{t("collab.notif_empty")}</p>
            </div>
          ) : (
            items.map(n => (
              <NotifItem key={n.id} n={n} onRead={markRead} />
            ))
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
